import { create } from 'zustand';
import { userApi, roleApi } from '../services/api';
import type { UserSummary, RoleSummary, Permission, CreateUserRequest, UpdateUserRequest, UpdatePasswordRequest, AssignRolesRequest } from '../types/auth';
import type { PagedData, ListQueryRequest } from '../types';

interface UserState {
  // 状态
  users: UserSummary[];
  roles: RoleSummary[];
  permissions: Permission[];
  total: number;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchUsers: (params: ListQueryRequest) => Promise<void>;
  fetchRoles: (params: ListQueryRequest) => Promise<void>;
  fetchPermissions: () => Promise<void>;
  createUser: (data: CreateUserRequest) => Promise<void>;
  updateUser: (id: string, data: UpdateUserRequest) => Promise<void>;
  updatePassword: (id: string, data: UpdatePasswordRequest) => Promise<void>;
  assignRoles: (id: string, data: AssignRolesRequest) => Promise<void>;
  activateUser: (id: string) => Promise<void>;
  deactivateUser: (id: string) => Promise<void>;
  createRole: (data: { name: string; description?: string; permissionIds: string[] }) => Promise<void>;
  updateRole: (id: string, data: { name: string; description?: string }) => Promise<void>;
  updateRolePermissions: (id: string, data: { permissionIds: string[] }) => Promise<void>;
  deleteRole: (id: string) => Promise<void>;
}

export const useUserStore = create<UserState>((set, get) => ({
  users: [],
  roles: [],
  permissions: [],
  total: 0,
  isLoading: false,
  error: null,

  fetchUsers: async (params: ListQueryRequest) => {
    set({ isLoading: true, error: null });
    try {
      const data = await userApi.getUsers(params);
      set({ 
        users: data.items || [], 
        total: data.total || 0,
        isLoading: false 
      });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  fetchRoles: async (params: ListQueryRequest) => {
    set({ isLoading: true, error: null });
    try {
      const data = await roleApi.getRoles(params);
      set({ 
        roles: data.items || [], 
        isLoading: false 
      });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  fetchPermissions: async () => {
    set({ isLoading: true, error: null });
    try {
      const data = await roleApi.getPermissions();
      set({ 
        permissions: data || [], 
        isLoading: false 
      });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  createUser: async (data: CreateUserRequest) => {
    set({ isLoading: true, error: null });
    try {
      await userApi.createUser(data);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  updateUser: async (id: string, data: UpdateUserRequest) => {
    set({ isLoading: true, error: null });
    try {
      await userApi.updateUser(id, data);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  updatePassword: async (id: string, data: UpdatePasswordRequest) => {
    set({ isLoading: true, error: null });
    try {
      await userApi.updatePassword(id, data);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  assignRoles: async (id: string, data: AssignRolesRequest) => {
    set({ isLoading: true, error: null });
    try {
      await userApi.assignRoles(id, data);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  activateUser: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await userApi.activateUser(id);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  deactivateUser: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await userApi.deactivateUser(id);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  createRole: async (data: { name: string; description?: string; permissionIds: string[] }) => {
    set({ isLoading: true, error: null });
    try {
      await roleApi.createRole(data as any);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  updateRole: async (id: string, data: { name: string; description?: string }) => {
    set({ isLoading: true, error: null });
    try {
      await roleApi.updateRole(id, data as any);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  updateRolePermissions: async (id: string, data: { permissionIds: string[] }) => {
    set({ isLoading: true, error: null });
    try {
      await roleApi.updateRolePermissions(id, data as any);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  deleteRole: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await roleApi.deleteRole(id);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },
}));
